/**
 * Copyright (C) 2026 Jugendstil, SLU - All Rights Reserved
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited. Proprietary and confidential.
 */
import { register } from '../registry.js';

/** Sub-pixel slack. A clamped paragraph is rarely a whole number of pixels taller than its box. */
const EPSILON = 1;

/**
 * A paragraph clamped to a few lines, with "See more" under it.
 *
 * `ClubAboutCard` on iOS is `lineLimit(isExpanded ? nil : 4)` and a plain button that flips it;
 * the biography card does the same at three lines. On a page the clamp is CSS and the state is one
 * attribute, so this file is the two lines of logic that attribute needs -- and one thing CSS
 * cannot answer on its own, which is whether the text was long enough to be clamped at all.
 *
 * The clamp is applied HERE and not by the stylesheet, which is the whole shape of the component.
 * With no script there is no `data-wp-expanded`, nothing is clamped and the toggle stays `hidden`:
 * the reader gets the entire text and no control. Clamping in CSS and revealing in script would
 * leave a page with no script showing four lines and a dead button under it.
 *
 * And the toggle is hidden when the text already fits, for the same reason the carousel hides an
 * arrow with nothing behind it: a control that does nothing says there is more to see.
 */
function partsOf(element: Element): { text: HTMLElement; toggle: HTMLElement } | null {
    const text = element.querySelector('.wp-expandable-text');
    const toggle = element.querySelector('.wp-expandable-toggle');

    if (!(text instanceof HTMLElement) || !(toggle instanceof HTMLElement)) {
        return null;
    }

    return { text, toggle };
}

/**
 * Collapses one block and decides whether its toggle is worth drawing.
 *
 * Measured with the clamp already applied, because that is the only state the two heights differ
 * in: `scrollHeight` is the text at its full height either way, and `clientHeight` is only shorter
 * than it while something is clipping it.
 *
 * A block a reader has already opened is left alone. A repaint arrives on a resize, after a Turbo
 * swap and whenever a page raises `wp:paint`, and none of those is a reason to close something
 * somebody opened.
 */
export function paintExpandable(element: Element): void {
    const parts = partsOf(element);
    if (parts === null || element.getAttribute('data-wp-expanded') === 'true') {
        return;
    }

    element.setAttribute('data-wp-expanded', 'false');
    parts.toggle.setAttribute('aria-expanded', 'false');

    const clamped = parts.text.scrollHeight - parts.text.clientHeight > EPSILON;

    parts.toggle.hidden = !clamped;

    /* Nothing to expand, so nothing to collapse either: the attribute comes back off, which also
       takes the clamp off the text rather than leaving it on a paragraph that fits inside it. */
    if (!clamped) {
        element.removeAttribute('data-wp-expanded');
    }
}

export function paint(root: ParentNode): void {
    root.querySelectorAll('.wp-expandable').forEach(paintExpandable);
}

export function listen(): void {
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const toggle = target.closest('.wp-expandable-toggle');
        const element = toggle?.closest('.wp-expandable');
        if (toggle === null || element == null) {
            return;
        }

        const expanded = element.getAttribute('data-wp-expanded') === 'true';

        element.setAttribute('data-wp-expanded', String(!expanded));
        toggle.setAttribute('aria-expanded', String(!expanded));
    });

    /* A narrower column is more lines, so a paragraph that fitted at 1280px may not fit at 390px
       and the other way round. Same signal the carousel's arrows are decided on. */
    window.addEventListener('resize', () => paint(document), { passive: true });

    register(paint);
}
